import { Component, OnInit } from '@angular/core';
import { Review } from './interfaces/review';
import { ReviewsService } from './services/reviews.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-company-reviews',
  templateUrl: './company-reviews.component.html',
  styleUrls: ['./company-reviews.component.css'],
})
export class CompanyReviewsComponent implements OnInit {
  activeTab: 'Pending' | 'Approved' | 'Declined' = 'Pending';
  searchQuery = '';
  reviews: Review[] = [];
  filteredReviews: Review[] = [];
  isLoading = false;

  constructor(private reviewService: ReviewsService) {}

  ngOnInit(): void {
    this.reviewService
    this.loadReviews();
  }

  // ─── Data Loading ─────────────────────────────────────────────────────────

  loadReviews(): void {
    this.isLoading = true;
    this.reviews = [];
    this.filteredReviews = [];

    if (this.activeTab === 'Pending') {
      this.reviewService.getPendingComments().subscribe({
        next: (res: any) => this.handleResponse(res),
        error: (err) => {
          console.error('Failed to load pending reviews', err);
          this.isLoading = false;
        },
      });
    } else if (this.activeTab === 'Approved') {
      this.reviewService.getAppovedComment().subscribe({
        next: (res: any) => this.handleResponse(res),
        error: (err) => {
          console.error('Failed to load approved reviews', err);
          this.isLoading = false;
        },
      });
    } else {
      // Declined — use getAllComment and filter locally
      this.reviewService.getAllComment().subscribe({
        next: (res: any) => {
          const all = this.mapResponseToReviews(res);
          this.reviews = all.filter((r) => r.status === 'Declined');
          this.filterReviews();
          this.isLoading = false;
        },
        error: (err) => {
          console.error('Failed to load declined reviews', err);
          this.isLoading = false;
        },
      });
    }
  }

  private handleResponse(res: any): void {
    this.reviews = this.mapResponseToReviews(res);
    this.filterReviews();
    this.isLoading = false;
  }

  // ─── Mapping ──────────────────────────────────────────────────────────────

  private mapResponseToReviews(res: any): Review[] {
    // Handle both paginated { content: [] } and plain array responses
    const items: any[] = Array.isArray(res) ? res : (res?.content ?? []);

    return items.map((item: any): Review => {
      const firstReply = item.replies?.[0] ?? null;

      return {
        id: item.id,
        customerName: item.userName ?? 'Unknown',
        customerInitial: (item.userName ?? 'U')[0].toUpperCase(),
        userPicture: item.userPicture ?? null,
        isVerified: false, // not in API — extend later if needed
        rating: item.rate ?? 0, // not in API — extend later if needed
        productName: '', // not in API — extend later if needed
        reviewText: item.content ?? '',
        timeAgo: this.formatTimeAgo(item.createdAt),
        status: this.mapStatus(item.status),
        hidden: item.hidden ?? false,
        storeReply: firstReply?.content ?? undefined,
        replyDate: firstReply?.createdAt
          ? new Date(firstReply.createdAt).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
            })
          : undefined,
        replyId: firstReply?.id ?? undefined,
      };
    });
  }

  // ─── Tab & Search ─────────────────────────────────────────────────────────

  setTab(tab: 'Pending' | 'Approved' | 'Declined'): void {
    this.activeTab = tab;
    this.loadReviews();
  }

  onSearch(): void {
    this.filterReviews();
  }

  filterReviews(): void {
    const query = this.searchQuery.toLowerCase().trim();
    this.filteredReviews = this.reviews.filter(
      (r) =>
        r.status === this.activeTab &&
        (!query ||
          r.customerName.toLowerCase().includes(query) ||
          r.id.toString().includes(query)),
    );
  }

  // ─── Actions ──────────────────────────────────────────────────────────────

  approveReview(id: string): void {
    this.reviewService
      .updateCommentStatus({ action: 'APPROVED' }, id)
      .subscribe({
        next: () => this.updateLocalStatus(id, 'Approved'),
        error: (err) => console.error('Failed to approve', err),
      });
  }

  declineReview(id: string): void {
    this.reviewService
      .updateCommentStatus({ action: 'DECLINED' }, id)
      .subscribe({
        next: () => this.updateLocalStatus(id, 'Declined'),
        error: (err) => console.error('Failed to decline', err),
      });
  }

  deleteReview(id: string): void {
    this.reviewService.deleteCommnet(id).subscribe({
      next: () => {
        this.reviews = this.reviews.filter((r) => r.id !== id);
        this.filterReviews();
      },
      error: (err) => console.error('Failed to delete', err),
    });
  }

  saveReply(event: { id: string; reply: string }): void {
    const review = this.reviews.find((r) => r.id === event.id);
    if (!review) return;

    if (review.replyId) {
      // Update existing reply
      this.reviewService
        .updateCommentReplay({ content: event.reply }, event.id, review.replyId)
        .subscribe({
          next: () => this.applyReplyLocally(event.id, event.reply),
          error: (err) => console.error('Failed to update reply', err),
        });
    } else {
      // Create new reply
      this.reviewService
        .addReplayToComment({ content: event.reply }, event.id)
        .subscribe({
          next: (res: any) => {
            review.replyId = res?.id ?? res?.data?.id;
            this.applyReplyLocally(event.id, event.reply);
          },
          error: (err) => console.error('Failed to add reply', err),
        });
    }
  }

  deleteReply(id: string): void {
    const review = this.reviews.find((r) => r.id === id);
    if (!review?.replyId) return;

    this.reviewService.deleteCommentReplay(id, review.replyId).subscribe({
      next: () => {
        review.storeReply = undefined;
        review.replyDate = undefined;
        review.replyId = undefined;
        this.filterReviews();
      },
      error: (err) => console.error('Failed to delete reply', err),
    });
  }

  toggleHide(id: string): void {
    const review = this.reviews.find((r) => r.id === id);
    if (!review) return;

    this.reviewService.changeCommentVisibilty(id).subscribe({
      next: () => {
        review.hidden = !review.hidden;
        this.filterReviews();
      },
      error: (err) => console.error('Failed to toggle visibility', err),
    });
  }

  // ─── Helpers ──────────────────────────────────────────────────────────────

  private updateLocalStatus(id: string, status: 'Approved' | 'Declined'): void {
    const review = this.reviews.find((r) => r.id === id);
    if (review) {
      review.status = status;
      this.filterReviews();
    }
  }

  private applyReplyLocally(id: string, replyText: string): void {
    const review = this.reviews.find((r) => r.id === id);
    if (review) {
      review.storeReply = replyText;
      review.replyDate = new Date().toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
      this.filterReviews();
    }
  }

  private mapStatus(apiStatus: string): 'Pending' | 'Approved' | 'Declined' {
    const map: Record<string, 'Pending' | 'Approved' | 'Declined'> = {
      PENDING: 'Pending',
      APPROVED: 'Approved',
      DECLINED: 'Declined',
      REJECTED: 'Declined',
    };
    return map[apiStatus?.toUpperCase()] ?? 'Pending';
  }

  private formatTimeAgo(dateStr?: string): string {
    if (!dateStr) return '';
    const diff = Date.now() - new Date(dateStr).getTime();
    const minutes = Math.floor(diff / 60000);
    if (minutes < 1) return 'just now';
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days} day${days > 1 ? 's' : ''} ago`;
  }
}
